import axios from 'axios';

// API base URL — localde localhost:8080'e, canlıda window.location.origin/api'ye gider
const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
export const API_BASE_URL = process.env.REACT_APP_API_URL || (isLocalHost ? 'http://localhost:8080/api' : `${window.location.origin}/api`);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Admin login/authenticate: do not send Bearer token (backend validates email/password)
    const isAdminLogin = (config.url || '').includes('/admins/login') || (config.url || '').includes('/admins/authenticate');
    if (!isAdminLogin) {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const isAdminRoute = window.location.pathname.startsWith('/admin');

    if (status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminToken');
      // Don't redirect if already on a login page
      const onLoginPage = window.location.pathname === '/login' || window.location.pathname === '/admin/login';
      if (!onLoginPage) {
        window.location.href = isAdminRoute ? '/admin/login' : '/login';
      }
    } else if (status === 429) {
      // Rate limited — attach friendly message for components to display
      error.friendlyMessage = 'Çok fazla istek gönderildi. Lütfen birkaç dakika bekleyin.';
    } else if (status >= 500) {
      error.friendlyMessage = 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.';
    } else if (!error.response) {
      // Network error (no response at all)
      error.isNetworkError = true;
      error.friendlyMessage = 'Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edin.';
    }

    return Promise.reject(error);
  }
);

// Product API
export const productAPI = {
  // Get all products
  getAllProducts: () => api.get('/products'),

  // Sayfalı ürün listesi — yeni ekranlar bunu kullanmalı; getAllProducts()
  // tüm kataloğu tek seferde çekiyor. Sunucu size'ı en fazla 100 ile sınırlar.
  getProductsPaginated: ({ page = 0, size = 20, sortBy = 'id', direction = 'asc' } = {}) =>
    api.get('/products/paginated', { params: { page, size, sortBy, direction } }),
  
  // Get product by ID
  getProductById: (id) => api.get(`/products/${id}`),
  
  // Get active products
  getActiveProducts: () => api.get('/products/active'),
  
  // Get products by category
  getProductsByCategory: (category) => api.get(`/products/category/${category}`),
  
  // Search products
  searchProducts: (query) => api.get(`/products/search?q=${query}`),
  
  // Get featured products
  getFeaturedProducts: () => api.get('/products/featured'),
  
  // Get products on sale
  getProductsOnSale: () => api.get('/products/on-sale'),
  
  // Get new products
  getNewProducts: () => api.get('/products/new'),
  
  // Get products by price range
  getProductsByPriceRange: (minPrice, maxPrice) => 
    api.get(`/products/price-range?min=${minPrice}&max=${maxPrice}`),
  
  // Admin methods
  createProduct: (productData) => api.post('/products', productData),
  
  updateProduct: (id, productData) => api.put(`/products/${id}`, productData),
  
  deleteProduct: (id) => api.delete(`/products/${id}`),
  
  activateProduct: (id) => api.post(`/products/${id}/activate`),
  
  deactivateProduct: (id) => api.post(`/products/${id}/deactivate`),
  
  increaseStock: (id, quantity) => api.post(`/products/${id}/increase-stock`, { quantity }),
  
  decreaseStock: (id, quantity) => api.post(`/products/${id}/decrease-stock`, { quantity }),

  // Statistics (admin/analytics)
  getProductStatistics: () => api.get('/products/statistics'),
  getLowStockProducts: () => api.get('/products/low-stock'),

  // Product images
  getImages: (id) => api.get(`/products/${id}/images`),
  addImage: (id, formData) => api.post(`/products/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (productId, imageId) => api.delete(`/products/${productId}/images/${imageId}`),
  updateImageSort: (productId, imageId, sortOrder) =>
    api.put(`/products/${productId}/images/${imageId}/sort`, { sortOrder }),

  // Product variants
  getVariants: (productId) => api.get(`/products/${productId}/variants`),
  getAllVariants: (productId) => api.get(`/products/${productId}/variants/all`),
  addVariant: (productId, data) => api.post(`/products/${productId}/variants`, data),
  updateVariant: (productId, variantId, data) => api.put(`/products/${productId}/variants/${variantId}`, data),
  deleteVariant: (productId, variantId) => api.delete(`/products/${productId}/variants/${variantId}`),

  // CSV bulk import
  importCsv: (formData) => api.post('/products/import/csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Auth API (JWT login + forgot/reset password)
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  validate: (token) => api.post('/auth/validate', { token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Google OAuth2 login: tam sayfa yönlendirme başlatır (XHR değildir, CORS'a takılmaz)
export const startGoogleOAuthLogin = () => {
  window.location.href = `${API_BASE_URL}/oauth2/authorization/google`;
};

// User API
export const userAPI = {
  // Register user
  register: (userData) => api.post('/users', userData),

  // Login user
  login: (credentials) => api.post('/users/authenticate', credentials),
  
  // Get all users
  getAllUsers: () => api.get('/users'),
  
  // Get active users
  getActiveUsers: () => api.get('/users/active'),
  
  // Get user by ID
  getUserById: (id) => api.get(`/users/${id}`),
  
  // Update user
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),

  // Update preferred payment method (kart verisi hiçbir zaman gönderilmez/saklanmaz — PCI-DSS)
  updatePaymentMethod: (id, paymentData) => api.put(`/users/${id}/payment-method`, paymentData),

  // Delete user
  deleteUser: (id) => api.delete(`/users/${id}`),
  
  // Get user orders (backend: /orders/user/{userId})
  getUserOrders: (userId) => api.get(`/orders/user/${userId}`),
  
  // Get user statistics
  getUserStatistics: () => api.get('/users/statistics'),
  
  // Email verification
  sendVerificationEmail: (email) => api.post('/users/send-verification-email', { email }),
  verifyEmail: (token) => api.post('/users/verify-email', { token }),
  checkEmailVerification: (userId) => api.get(`/users/${userId}/email-verification-status`),
};

// Order API
export const orderAPI = {
  // Create order
  createOrder: (orderData) => api.post('/orders', orderData),
  
  // Get all orders
  getAllOrders: () => api.get('/orders'),
  
  // Get order by ID
  getOrderById: (id) => api.get(`/orders/${id}`),
  
  // Get order by order number
  getOrderByOrderNumber: (orderNumber) => api.get(`/orders/order-number/${orderNumber}`),
  
  // Get orders by user
  getOrdersByUser: (userId) => api.get(`/orders/user/${userId}`),
  
  // Get orders by status
  getOrdersByStatus: (status) => api.get(`/orders/status/${status}`),
  
  // Update order status
  updateOrderStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),

  // Bulk update order status (admin)
  bulkUpdateOrderStatus: (orderIds, status) => api.put('/orders/bulk-status', { orderIds, status }),
  
  // Cancel order
  cancelOrder: (id, reason) => api.post(`/orders/${id}/cancel`, { reason }),

  // Get orders by payment status (e.g. REFUND_FAILED — admin recovery screen)
  getOrdersByPaymentStatus: (paymentStatus) => api.get(`/orders/payment-status/${paymentStatus}`),

  // Admin: retry a previously failed gateway refund
  retryRefund: (id) => api.post(`/orders/${id}/retry-refund`),

  // Admin: mark a failed-refund order as refunded outside the gateway (e.g. bank transfer)
  markRefundedManually: (id, note) => api.post(`/orders/${id}/mark-refunded-manually`, { note }),
  
  // Add tracking number (also flips the order to SHIPPED and emails the customer)
  addTrackingNumber: (id, trackingNumber, cargoCompany) =>
    api.post(`/orders/${id}/tracking`, { trackingNumber, cargoCompany }),
  
  // Delete order
  deleteOrder: (id) => api.delete(`/orders/${id}`),
  
  // Get order statistics
  getOrderStatistics: () => api.get('/orders/statistics'),

  // Get order for invoice (same as getOrderById; use for print/export)
  getOrderForInvoice: (id) => api.get(`/orders/${id}`),

  // Get status history for an order
  getStatusHistory: (id) => api.get(`/orders/${id}/status-history`),

  // Sipariş numarasıyla — misafir takibinde kullanılır (numara erişim anahtarıdır)
  getStatusHistoryByNumber: (orderNumber) =>
    api.get(`/orders/order-number/${orderNumber}/status-history`),

  // Download PDF invoice
  downloadInvoice: (id) => api.get(`/orders/${id}/invoice`, { responseType: 'blob' }),

  // Sipariş numarasıyla — misafir de kendi faturasını indirebilir
  downloadInvoiceByNumber: (orderNumber) =>
    api.get(`/orders/order-number/${orderNumber}/invoice`, { responseType: 'blob' }),

  // Export orders as CSV
  exportCsv: (params = {}) => api.get('/orders/export/csv', { params, responseType: 'blob' }),
};

// Category API
export const categoryAPI = {
  // Get all categories
  getAllCategories: () => api.get('/categories'),

  // Get active categories (ordered by displayOrder)
  getActiveCategories: () => api.get('/categories/active'),

  // Get category by ID
  getCategoryById: (id) => api.get(`/categories/${id}`),
  
  // Create category
  createCategory: (categoryData) => api.post('/categories', categoryData),
  
  // Update category
  updateCategory: (id, categoryData) => api.put(`/categories/${id}`, categoryData),
  
  // Delete category
  deleteCategory: (id) => api.delete(`/categories/${id}`),
};

// Banner API
export const bannerAPI = {
  // Get all banners
  getAllBanners: () => api.get('/banners'),
  
  // Get active banners
  getActiveBanners: () => api.get('/banners/active'),
  
  // Get valid banners
  getValidBanners: () => api.get('/banners/valid'),
  
  // Get hero banners
  getHeroBanners: () => api.get('/banners/hero'),
  
  // Get banners by type
  getBannersByType: (type) => api.get(`/banners/type/${type}`),
  
  // Get banners by position
  getBannersByPosition: (position) => api.get(`/banners/position/${position}`),
  
  // Get banner by ID
  getBannerById: (id) => api.get(`/banners/${id}`),
  
  // Create banner
  createBanner: (bannerData, adminId) => api.post('/banners', bannerData, { params: { adminId } }),
  
  // Update banner
  updateBanner: (id, bannerData) => api.put(`/banners/${id}`, bannerData),
  
  // Delete banner
  deleteBanner: (id) => api.delete(`/banners/${id}`),
  
  // Toggle banner active status
  toggleBanner: (id) => api.post(`/banners/${id}/toggle`),
  
  // Update display order
  updateDisplayOrder: (id, displayOrder) => api.put(`/banners/${id}/order`, { displayOrder }),
  
  // Get expired banners
  getExpiredBanners: () => api.get('/banners/expired'),
  
  // Get banners expiring soon
  getBannersExpiringSoon: (days = 7) => api.get(`/banners/expiring-soon?days=${days}`),
};

// Campaign API (paket kampanyalar — birden fazla ürünü paket fiyatına bağlayan afişler)
export const campaignAPI = {
  getAllCampaigns: () => api.get('/campaigns'),
  getActiveCampaigns: () => api.get('/campaigns/active'),
  getValidCampaigns: () => api.get('/campaigns/valid'),
  getCampaignById: (id) => api.get(`/campaigns/${id}`),
  createCampaign: (campaignData) => api.post('/campaigns', campaignData),
  updateCampaign: (id, campaignData) => api.put(`/campaigns/${id}`, campaignData),
  deleteCampaign: (id) => api.delete(`/campaigns/${id}`),
  toggleCampaign: (id) => api.post(`/campaigns/${id}/toggle`),
};

// Content Item API (repeatable page blocks: FAQ, why-us cards, promo strip, menu badges)
export const contentItemAPI = {
  // Get every published item across all sections (site side)
  getActiveItems: () => api.get('/content-items/active'),

  // Get all items including inactive ones (admin)
  getAllItems: () => api.get('/content-items'),

  // Get items of one section
  getItemsBySection: (section) => api.get(`/content-items/section/${section}`),

  // Create item
  createItem: (data) => api.post('/content-items', data),

  // Update item structure (icon, image, link, order, active)
  updateItem: (id, data) => api.put(`/content-items/${id}`, data),

  // Delete item
  deleteItem: (id) => api.delete(`/content-items/${id}`),

  // Toggle active status
  toggleItem: (id) => api.post(`/content-items/${id}/toggle`),

  // Update display order
  updateDisplayOrder: (id, displayOrder) => api.put(`/content-items/${id}/order`, { displayOrder }),
};

// Translation API (admin-editable site texts; overrides the static locale files)
export const translationAPI = {
  // Get overrides for one language as a flat { key: value } map
  getTranslationsByLang: (lang) => api.get(`/translations/lang/${lang}`),

  // Get every override row (admin)
  getAllTranslations: () => api.get('/translations'),

  // Get all languages of a single key
  getTranslationsForKey: (key) => api.get(`/translations/key/${key}`),

  // Save one key across languages: { tr, en, fr, nl }
  saveTranslations: (key, valuesByLang) => api.put(`/translations/key/${key}`, valuesByLang),

  // Reset a key back to its default text
  resetKey: (key) => api.delete(`/translations/key/${key}`),

  // Reset a single language of a key
  resetKeyForLang: (key, lang) => api.delete(`/translations/key/${key}/lang/${lang}`),
};

// Subcategory API (navbar mega menu sub-items)
export const subcategoryAPI = {
  // Get all subcategories
  getAllSubcategories: () => api.get('/subcategories'),

  // Get subcategories of a category
  getSubcategoriesByCategory: (categoryId) => api.get(`/subcategories/category/${categoryId}`),

  // Get subcategory by ID
  getSubcategoryById: (id) => api.get(`/subcategories/${id}`),

  // Create subcategory under a category
  createSubcategory: (categoryId, data) => api.post(`/subcategories/category/${categoryId}`, data),

  // Update subcategory
  updateSubcategory: (id, data) => api.put(`/subcategories/${id}`, data),

  // Move subcategory to another category
  moveToCategory: (id, categoryId) => api.put(`/subcategories/${id}/category/${categoryId}`),

  // Delete subcategory
  deleteSubcategory: (id) => api.delete(`/subcategories/${id}`),

  // Toggle subcategory active status
  toggleSubcategory: (id) => api.post(`/subcategories/${id}/toggle`),

  // Update display order
  updateDisplayOrder: (id, displayOrder) => api.put(`/subcategories/${id}/order`, { displayOrder }),
};

// Collection API (homepage collection cards)
export const collectionAPI = {
  // Get all collections
  getAllCollections: () => api.get('/collections'),

  // Get active collections
  getActiveCollections: () => api.get('/collections/active'),

  // Get collection by ID
  getCollectionById: (id) => api.get(`/collections/${id}`),

  // Create collection
  createCollection: (collectionData) => api.post('/collections', collectionData),

  // Update collection
  updateCollection: (id, collectionData) => api.put(`/collections/${id}`, collectionData),

  // Delete collection
  deleteCollection: (id) => api.delete(`/collections/${id}`),

  // Toggle collection active status
  toggleCollection: (id) => api.post(`/collections/${id}/toggle`),

  // Update display order
  updateDisplayOrder: (id, displayOrder) => api.put(`/collections/${id}/order`, { displayOrder }),
};

// Notification API
export const notificationAPI = {
  // Get user notification preferences
  getUserPreferences: (userId) => api.get(`/notifications/preferences/${userId}`),
  
  // Update user notification preferences
  updateUserPreferences: (userId, preferences) => api.put(`/notifications/preferences/${userId}`, preferences),
  
  // Get user email history
  getUserEmails: (userId, page = 0, size = 10) => api.get(`/notifications/emails/${userId}?page=${page}&size=${size}`),
  
  // Send test email
  sendTestEmail: (emailData) => api.post('/notifications/test-email', emailData),
  
  // Process pending emails (admin)
  processPendingEmails: () => api.post('/notifications/process-pending'),
  
  // Get email statistics (admin)
  getEmailStats: () => api.get('/notifications/stats'),
};

// Admin API
export const adminAPI = {
  // Admin login
  login: (credentials) => api.post('/admins/login', credentials),
  
  // Get all admins
  getAllAdmins: () => api.get('/admins'),
  
  // Get admin by ID
  getAdminById: (id) => api.get(`/admins/${id}`),
  
  // Create admin
  createAdmin: (adminData) => api.post('/admins', adminData),
  
  // Update admin
  updateAdmin: (id, adminData) => api.put(`/admins/${id}`, adminData),
  
  // Delete admin
  deleteAdmin: (id) => api.delete(`/admins/${id}`),

  // Oturumdaki yöneticinin kendi kaydı (rol kontrolü için)
  getCurrentAdmin: () => api.get('/admins/me'),

  // Yetki atama — yalnızca süper yönetici
  changeRole: (id, role) => api.put(`/admins/${id}/role`, { role }),

  // Aktif / pasif
  activateAdmin: (id) => api.post(`/admins/${id}/activate`),
  deactivateAdmin: (id) => api.post(`/admins/${id}/deactivate`),
};

// Coupon API
export const couponAPI = {
  // Get all coupons
  getAllCoupons: () => api.get('/coupons'),
  
  // Get active coupons
  getActiveCoupons: () => api.get('/coupons/active'),
  
  // Get public coupons
  getPublicCoupons: () => api.get('/coupons/public'),
  
  // Get valid coupons
  getValidCoupons: () => api.get('/coupons/valid'),
  
  // Get coupon by ID
  getCouponById: (id) => api.get(`/coupons/${id}`),
  
  // Get coupon by code
  getCouponByCode: (code) => api.get(`/coupons/code/${code}`),
  
  // Create coupon
  createCoupon: (couponData) => api.post('/coupons', couponData),
  
  // Update coupon
  updateCoupon: (id, couponData) => api.put(`/coupons/${id}`, couponData),
  
  // Delete coupon
  deleteCoupon: (id) => api.delete(`/coupons/${id}`),
  
  // Activate coupon
  activateCoupon: (id) => api.post(`/coupons/${id}/activate`),
  
  // Deactivate coupon
  deactivateCoupon: (id) => api.post(`/coupons/${id}/deactivate`),
  
  // Validate coupon
  validateCoupon: (code, orderAmount) => api.post('/coupons/validate', { code, orderAmount }),
  
  // Calculate discount
  calculateDiscount: (code, orderAmount) => api.post('/coupons/calculate-discount', { code, orderAmount }),
  
  // Use coupon
  useCoupon: (couponData) => api.post('/coupons/use', couponData),
  
  // Use coupon by email
  useCouponByEmail: (couponData) => api.post('/coupons/use-by-email', couponData),
  
  // Get coupon usage history
  getCouponUsageHistory: (id, page = 0, size = 10) => api.get(`/coupons/${id}/usage?page=${page}&size=${size}`),
  
  // Get user's coupon usage history
  getUserCouponUsageHistory: (userId, page = 0, size = 10) => api.get(`/coupons/user/${userId}/usage?page=${page}&size=${size}`),
  
  // Get available coupons for user
  getAvailableCouponsForUser: (userId) => api.get(`/coupons/available/user/${userId}`),
  
  // Get available coupons for order amount
  getAvailableCouponsForOrderAmount: (orderAmount) => api.get(`/coupons/available/order-amount?orderAmount=${orderAmount}`),
  
  // Get available coupons for user and order amount
  getAvailableCouponsForUserAndOrderAmount: (userId, orderAmount) => api.get(`/coupons/available/user/${userId}/order-amount?orderAmount=${orderAmount}`),
  
  // Check if user can use coupon
  canUserUseCoupon: (code, userId) => api.get(`/coupons/can-use/${code}/user/${userId}`),
  
  // Get coupon statistics
  getCouponStatistics: () => api.get('/coupons/statistics'),
  
  // Get most used coupons
  getMostUsedCoupons: (limit = 10) => api.get(`/coupons/most-used?limit=${limit}`),
  
  // Get coupon usage statistics
  getCouponUsageStatistics: (limit = 10) => api.get(`/coupons/usage-statistics?limit=${limit}`),

  // Send coupon by email to list of addresses
  sendCouponEmails: (couponId, emails) => api.post('/coupons/send-emails', { couponId, emails }),
};

// Product Review API
export const productReviewAPI = {
  // Create review
  createReview: (reviewData) => api.post('/product-reviews', reviewData),
  
  // Get reviews by product
  getReviewsByProduct: (productId) => api.get(`/product-reviews/product/${productId}`),
  
  // Get reviews by product with pagination (backend: /product-reviews/product/{id}/paginated)
  getReviewsByProductPaginated: (productId, page = 0, size = 10) => api.get(`/product-reviews/product/${productId}/paginated?page=${page}&size=${size}`),
  
  // Get reviews by user
  getReviewsByUser: (userId) => api.get(`/product-reviews/user/${userId}`),
  
  // Get reviews by user with pagination
  getReviewsByUserPaginated: (userId, page = 0, size = 10) => api.get(`/product-reviews/user/${userId}?page=${page}&size=${size}`),
  
  // Get review by ID
  getReviewById: (reviewId) => api.get(`/product-reviews/${reviewId}`),
  
  // Update review
  updateReview: (reviewId, reviewData) => api.put(`/product-reviews/${reviewId}`, reviewData),
  
  // Delete review
  deleteReview: (reviewId) => api.delete(`/product-reviews/${reviewId}`),
  
  // Approve review
  approveReview: (reviewId) => api.post(`/product-reviews/${reviewId}/approve`),
  
  // Reject review
  rejectReview: (reviewId) => api.post(`/product-reviews/${reviewId}/reject`),
  
  // Get pending reviews
  getPendingReviews: () => api.get('/product-reviews/pending'),
  
  // Get pending reviews with pagination (backend: /product-reviews/pending/paginated)
  getPendingReviewsPaginated: (page = 0, size = 10) => api.get(`/product-reviews/pending/paginated?page=${page}&size=${size}`),
  
  // Get reviews by rating
  getReviewsByRating: (productId, rating) => api.get(`/product-reviews/product/${productId}/rating/${rating}`),
  
  // Get verified purchase reviews
  getVerifiedPurchaseReviews: (productId) => api.get(`/product-reviews/product/${productId}/verified`),
  
  // Get most helpful reviews
  getMostHelpfulReviews: (productId) => api.get(`/product-reviews/product/${productId}/helpful`),
  
  // Mark review as helpful
  markAsHelpful: (reviewId) => api.post(`/product-reviews/${reviewId}/helpful`),
  
  // Mark review as not helpful
  markAsNotHelpful: (reviewId) => api.post(`/product-reviews/${reviewId}/not-helpful`),
  
  // Get average rating
  getAverageRating: (productId) => api.get(`/product-reviews/product/${productId}/average-rating`),
  
  // Get rating distribution
  getRatingDistribution: (productId) => api.get(`/product-reviews/product/${productId}/rating-distribution`),
  
  // Get review count
  getReviewCount: (productId) => api.get(`/product-reviews/product/${productId}/count`),
  
  // Get review count by rating
  getReviewCountByRating: (productId, rating) => api.get(`/product-reviews/product/${productId}/count/rating/${rating}`),
  
  // Search reviews
  searchReviews: (productId, keyword) => api.get(`/product-reviews/product/${productId}/search?keyword=${keyword}`),
  
  // Search reviews with pagination
  searchReviewsPaginated: (productId, keyword, page = 0, size = 10) => api.get(`/product-reviews/product/${productId}/search?keyword=${keyword}&page=${page}&size=${size}`),
  
  // Get top rated products
  getTopRatedProducts: (minReviewCount = 5, limit = 10) => api.get(`/product-reviews/top-rated?minReviewCount=${minReviewCount}&limit=${limit}`),
  
  // Get most reviewed products
  getMostReviewedProducts: (limit = 10) => api.get(`/product-reviews/most-reviewed?limit=${limit}`),
  
  // Get recent reviews
  getRecentReviews: (limit = 10) => api.get(`/product-reviews/recent?limit=${limit}`),
  
  // Get review statistics
  getReviewStatistics: () => api.get('/product-reviews/statistics'),
  
  // Check if user can review product
  canUserReviewProduct: (userId, productId) => api.get(`/product-reviews/can-review/user/${userId}/product/${productId}`),
  
  // Get user's review for product
  getUserReviewForProduct: (userId, productId) => api.get(`/product-reviews/user/${userId}/product/${productId}`),
};

// Support Ticket API
export const supportTicketAPI = {
  // Create support ticket
  createTicket: (ticketData) => api.post('/support-tickets', ticketData),
  
  // Get all tickets
  getAllTickets: () => api.get('/support-tickets'),
  
  // Get tickets with pagination (backend: /support-tickets/paginated)
  getTicketsPaginated: (page = 0, size = 10) => api.get(`/support-tickets/paginated?page=${page}&size=${size}`),
  
  // Get ticket by ID
  getTicketById: (id) => api.get(`/support-tickets/${id}`),
  
  // Get ticket by ticket number
  getTicketByNumber: (ticketNumber) => api.get(`/support-tickets/number/${ticketNumber}`),
  
  // Get tickets by user
  getTicketsByUser: (userId) => api.get(`/support-tickets/user/${userId}`),
  
  // Get tickets by user with pagination
  getTicketsByUserPaginated: (userId, page = 0, size = 10) => api.get(`/support-tickets/user/${userId}?page=${page}&size=${size}`),
  
  // Get tickets by email
  getTicketsByEmail: (email) => api.get(`/support-tickets/email/${email}`),
  
  // Get tickets by email with pagination
  getTicketsByEmailPaginated: (email, page = 0, size = 10) => api.get(`/support-tickets/email/${email}?page=${page}&size=${size}`),
  
  // Get tickets by status
  getTicketsByStatus: (status) => api.get(`/support-tickets/status/${status}`),
  
  // Get tickets by category
  getTicketsByCategory: (category) => api.get(`/support-tickets/category/${category}`),
  
  // Get tickets by priority
  getTicketsByPriority: (priority) => api.get(`/support-tickets/priority/${priority}`),
  
  // Get unassigned tickets
  getUnassignedTickets: () => api.get('/support-tickets/unassigned'),
  
  // Update ticket
  updateTicket: (id, ticketData) => api.put(`/support-tickets/${id}`, ticketData),
  
  // Assign ticket to admin
  assignTicket: (id, adminId) => api.post(`/support-tickets/${id}/assign`, { adminId }),
  
  // Update ticket status
  updateTicketStatus: (id, status) => api.put(`/support-tickets/${id}/status`, { status }),
  
  // Update ticket priority
  updateTicketPriority: (id, priority) => api.put(`/support-tickets/${id}/priority`, { priority }),
  
  // Add admin notes
  addAdminNotes: (id, notes) => api.post(`/support-tickets/${id}/notes`, { notes }),
  
  // Resolve ticket
  resolveTicket: (id, resolution) => api.post(`/support-tickets/${id}/resolve`, { resolution }),
  
  // Close ticket
  closeTicket: (id) => api.post(`/support-tickets/${id}/close`),
  
  // Delete ticket
  deleteTicket: (id) => api.delete(`/support-tickets/${id}`),
  
  // Search tickets
  searchTickets: (keyword) => api.get(`/support-tickets/search?keyword=${keyword}`),
  
  // Search tickets with pagination
  searchTicketsPaginated: (keyword, page = 0, size = 10) => api.get(`/support-tickets/search?keyword=${keyword}&page=${page}&size=${size}`),
  
  // Get ticket statistics
  getTicketStatistics: () => api.get('/support-tickets/statistics'),
  
  // Get tickets created today
  getTicketsCreatedToday: () => api.get('/support-tickets/today'),
  
  // Get tickets created this week
  getTicketsCreatedThisWeek: () => api.get('/support-tickets/this-week'),
  
  // Get tickets created this month
  getTicketsCreatedThisMonth: () => api.get('/support-tickets/this-month'),
  
  // Get overdue tickets
  getOverdueTickets: (days = 7) => api.get(`/support-tickets/overdue?days=${days}`),
};

// File Upload API
export const uploadAPI = {
  // Upload product image
  uploadProductImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/product', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Upload banner image
  uploadBannerImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/banner', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Upload collection image
  uploadCollectionImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/collection', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Settings API
export const settingAPI = {
  // Get all settings as map
  getAllSettings: () => api.get('/settings'),
  
  // Get all settings as list (admin only)
  getAllSettingsList: () => api.get('/settings/all'),
  
  // Get setting value by key
  getSettingValue: (key) => api.get(`/settings/${key}`),
  
  // Create setting (admin only)
  createSetting: (setting) => api.post('/settings', setting),
  
  // Update setting (admin only)
  updateSetting: (key, setting) => api.put(`/settings/${key}`, setting),
  
  // Delete setting (admin only)
  deleteSetting: (key) => api.delete(`/settings/${key}`),
};

// Cargo Settings API
export const cargoSettingsAPI = {
  // Get all city cargo settings
  getAllCityCargoSettings: () => api.get('/cargo-settings'),
  
  // Get active city cargo settings
  getActiveCityCargoSettings: () => api.get('/cargo-settings/active'),
  
  // Get city cargo settings by ID
  getCityCargoSettingsById: (id) => api.get(`/cargo-settings/${id}`),
  
  // Get city cargo settings by city name
  getCityCargoSettingsByCityName: (cityName) => api.get(`/cargo-settings/city/${cityName}`),
  
  // Get city cargo settings by city code
  getCityCargoSettingsByCityCode: (cityCode) => api.get(`/cargo-settings/code/${cityCode}`),
  
  // Search cities by name
  searchCitiesByName: (cityName) => api.get(`/cargo-settings/search?cityName=${cityName}`),
  
  // Get cargo settings for a specific product
  getCargoSettingsForProduct: (productId) => api.get(`/cargo-settings/product/${productId}`),
  
  // Create new city cargo settings
  createCityCargoSettings: (cityCargoSettings) => api.post('/cargo-settings', cityCargoSettings),
  
  // Update existing city cargo settings
  updateCityCargoSettings: (id, cityCargoSettings) => api.put(`/cargo-settings/${id}`, cityCargoSettings),
  
  // Toggle active status
  toggleActiveStatus: (id) => api.patch(`/cargo-settings/${id}/toggle-active`),
  
  // Delete city cargo settings
  deleteCityCargoSettings: (id) => api.delete(`/cargo-settings/${id}`),
  
  // Calculate cargo cost
  calculateCargoCost: (cityCode, orderAmount) => api.post('/cargo-settings/calculate-cost', { cityCode, orderAmount }),
  
  // Calculate estimated delivery date
  calculateEstimatedDeliveryDate: (cityCode, orderDate) => api.post('/cargo-settings/calculate-delivery-date', { cityCode, orderDate }),
  
  // Get delivery days for a city
  getDeliveryDaysForCity: (cityCode) => api.get(`/cargo-settings/${cityCode}/delivery-days`),
  
  // Check if specific day is available for delivery
  checkDeliveryDayAvailability: (cityCode, dayOfWeek) => api.get(`/cargo-settings/${cityCode}/delivery-days/${dayOfWeek}`),
  
  // Get cities with free shipping
  getCitiesWithFreeShipping: (orderAmount) => api.post('/cargo-settings/free-shipping', { orderAmount }),
  
  // Assign cargo settings to product
  assignCargoSettingsToProduct: (productId, cityCargoSettingsIds) => api.post(`/cargo-settings/product/${productId}/assign`, { cityCargoSettingsIds }),
  
  // Remove cargo settings from product
  removeCargoSettingsFromProduct: (productId, cityCargoSettingsIds) => api.post(`/cargo-settings/product/${productId}/remove`, { cityCargoSettingsIds }),
};

// Delivery Schedule API
export const deliveryScheduleAPI = {
  // Get all delivery schedules
  getAllDeliverySchedules: () => api.get('/delivery-schedules'),
  
  // Get active delivery schedules
  getActiveDeliverySchedules: () => api.get('/delivery-schedules/active'),
  
  // Get delivery schedule by ID
  getDeliveryScheduleById: (id) => api.get(`/delivery-schedules/${id}`),
  
  // Get delivery schedules by city name
  getDeliverySchedulesByCityName: (cityName) => api.get(`/delivery-schedules/city/${cityName}`),
  
  // Get delivery schedules by city code
  getDeliverySchedulesByCityCode: (cityCode) => api.get(`/delivery-schedules/code/${cityCode}`),
  
  // Get active delivery schedules for a specific date
  getActiveSchedulesByDate: (date) => api.get(`/delivery-schedules/date/${date}`),
  
  // Get active delivery schedules for a specific city and date
  getActiveSchedulesByCityAndDate: (cityCode, date) => api.get(`/delivery-schedules/city/${cityCode}/date/${date}`),
  
  // Get active delivery schedules for a specific city and day of week
  getActiveSchedulesByCityAndDay: (cityCode, dayOfWeek) => api.get(`/delivery-schedules/city/${cityCode}/day/${dayOfWeek}`),
  
  // Check if delivery is available for a specific city, date and day
  checkDeliveryAvailability: (cityCode, date, dayOfWeek) => api.get(`/delivery-schedules/availability/${cityCode}/${date}/${dayOfWeek}`),
  
  // Get available delivery days for a city on a specific date
  getAvailableDeliveryDays: (cityCode, date) => api.get(`/delivery-schedules/available-days/${cityCode}/${date}`),
  
  // Get available delivery dates for a city in a date range
  getAvailableDeliveryDates: (cityCode, startDate, endDate) => api.get(`/delivery-schedules/available-dates/${cityCode}?startDate=${startDate}&endDate=${endDate}`),
  
  // Create new delivery schedule
  createDeliverySchedule: (deliverySchedule) => api.post('/delivery-schedules', deliverySchedule),
  
  // Update existing delivery schedule
  updateDeliverySchedule: (id, deliverySchedule) => api.put(`/delivery-schedules/${id}`, deliverySchedule),
  
  // Toggle active status
  toggleActiveStatus: (id) => api.patch(`/delivery-schedules/${id}/toggle-active`),
  
  // Delete delivery schedule
  deleteDeliverySchedule: (id) => api.delete(`/delivery-schedules/${id}`),
  
  // Get expired delivery schedules
  getExpiredSchedules: () => api.get('/delivery-schedules/expired'),
  
  // Get upcoming delivery schedules
  getUpcomingSchedules: () => api.get('/delivery-schedules/upcoming'),
  
  // Get current delivery schedules
  getCurrentSchedules: () => api.get('/delivery-schedules/current'),
  
  // Get delivery schedules expiring soon
  getSchedulesExpiringSoon: (days = 7) => api.get(`/delivery-schedules/expiring-soon?days=${days}`),
  
  // Search delivery schedules by name
  searchSchedulesByName: (name) => api.get(`/delivery-schedules/search?name=${name}`),
  
  // Get delivery schedules by month and year
  getSchedulesByMonthAndYear: (year, month) => api.get(`/delivery-schedules/month/${year}/${month}`),
  
  // Get delivery schedule statistics
  getDeliveryScheduleStatistics: () => api.get('/delivery-schedules/statistics'),
  
  // Check if schedule is currently active
  getScheduleActiveStatus: (id) => api.get(`/delivery-schedules/${id}/active-status`),
  
  // Get available delivery options for a city (for frontend checkout)
  getDeliveryOptionsForCity: (cityCode) => api.get(`/delivery-schedules/city/${cityCode}/delivery-options`),

  // Şehir adına göre önümüzdeki teslimat tarihlerini getir
  getUpcomingDates: (cityName, count = 5) =>
    api.get('/delivery-schedules/upcoming-dates', { params: { cityName, count } }),
};

// Wishlist API
export const wishlistAPI = {
  getProductIds: (userId) => api.get(`/wishlist/${userId}`),
  getProducts: (userId) => api.get(`/wishlist/${userId}/products`),
  add: (userId, productId) => api.post(`/wishlist/${userId}/add`, { productId }),
  remove: (userId, productId) => api.delete(`/wishlist/${userId}/remove/${productId}`),
  sync: (userId, productIds) => api.post(`/wishlist/${userId}/sync`, { productIds }),
  check: (userId, productId) => api.get(`/wishlist/${userId}/check/${productId}`),
};

// Customer activity/preference tracking API
export const activityAPI = {
  track: (sessionId, productId, activityType) =>
    api.post('/activity/track', { sessionId, productId, activityType }),
  merge: (sessionId) => api.post('/activity/merge', { sessionId }),
  getRecommendations: (sessionId, limit = 12) =>
    api.get('/activity/recommendations', { params: { sessionId, limit } }),
  getCustomerPreferences: (userId) => api.get(`/activity/admin/user/${userId}`),
};

// Favoriye ekleme teşekkür kuponu — admin ayarları
export const wishlistCouponAPI = {
  getSettings: () => api.get('/wishlist-coupon-settings'),
  updateSettings: (settings) => api.put('/wishlist-coupon-settings', settings),
};

// Teslimat sonrası teşekkür kuponu — admin ayarları (favori kuponundan bağımsız)
export const deliveryCouponAPI = {
  getSettings: () => api.get('/delivery-coupon-settings'),
  updateSettings: (settings) => api.put('/delivery-coupon-settings', settings),
};

// Sepetten çıkarma kuponu — admin ayarları + müşteri tetikleme (favori/teslimat kuponundan bağımsız)
export const cartRemovalCouponAPI = {
  getSettings: () => api.get('/cart-removal-coupon-settings'),
  updateSettings: (settings) => api.put('/cart-removal-coupon-settings', settings),
  trigger: () => api.post('/cart-removal-coupon/trigger'),
};

// Return/Refund API
export const returnAPI = {
  createReturn: (data) => api.post('/returns', data),
  getReturnsByOrder: (orderId) => api.get(`/returns/order/${orderId}`),
  getReturnsByUser: (userId) => api.get(`/returns/user/${userId}`),
  getAllReturns: (status) => api.get('/returns', { params: status ? { status } : {} }),
  getReturn: (id) => api.get(`/returns/${id}`),
  updateStatus: (id, status, adminNote) => api.put(`/returns/${id}/status`, { status, adminNote }),
};

// Audit Log API (admin)
export const auditLogAPI = {
  getLogs: (page = 0, size = 50) => api.get('/admin/audit-logs', { params: { page, size } }),
};

// Payment API
export const paymentAPI = {
  // Public: aktif gateway listesi + Stripe publishable key
  getPublicSettings: () => api.get('/payments/settings/public'),

  // Stripe: PaymentIntent oluştur
  createStripeIntent: (amount, currency, orderNumber) =>
    api.post('/payments/stripe/create-intent', { amount, currency, orderNumber }),

  // Mollie: ödeme oluştur
  createMolliePayment: (amount, currency, orderNumber, redirectUrl, webhookUrl) =>
    api.post('/payments/mollie/create-payment', { amount, currency, orderNumber, redirectUrl, webhookUrl }),

  // Admin: gateway ayarlarını oku
  getAdminSettings: () => api.get('/payments/admin/settings'),

  // Admin: gateway ayarlarını kaydet
  updateAdminSettings: (settings) => api.put('/payments/admin/settings', settings),
};

// Email Template API (admin)
export const emailTemplateAPI = {
  listTemplates: () => api.get('/admin/email-templates'),
  getTemplate: (templateKey, language) => api.get(`/admin/email-templates/${templateKey}/${language}`),
  saveTemplate: (templateKey, language, subject, body) =>
    api.put(`/admin/email-templates/${templateKey}/${language}`, { subject, body }),
  resetTemplate: (templateKey, language) => api.delete(`/admin/email-templates/${templateKey}/${language}`),
};

// 3D Flipbook Katalog API (Admin & Vitrin)
export const flipbookAPI = {
  // Katalog verilerini getir
  getFlipbookData: async () => {
    try {
      const res = await api.get('/settings/flipbook_catalog_data');
      if (res && res.data) {
        if (typeof res.data === 'string' && res.data.trim().startsWith('[')) {
          return JSON.parse(res.data);
        } else if (Array.isArray(res.data)) {
          return res.data;
        }
      }
      return null;
    } catch {
      return null;
    }
  },

  // Admin: Katalog sayfalarını kaydet
  saveFlipbookData: async (data) => {
    const payload = {
      key: 'flipbook_catalog_data',
      value: JSON.stringify(data),
      category: 'FLIPBOOK',
      description: '3D Flipbook Katalog Sayfa Verileri',
    };
    try {
      return await api.put('/settings/flipbook_catalog_data', payload);
    } catch {
      return await api.post('/settings', payload);
    }
  },
};

export default api;

